import {Questionary} from "../../model/questionary";
import m, {Vnode} from "mithril";
import {FormItem} from "../../model/form/formItem";
import FormGroup from "../../model/form/formGroup";
import {Form} from "../../model/form/form";

type Progress = {
    form: Form,
    currentGroup: number,
    progress: number,
    formIndexInGroup: number,
    caption: string|null,
    progressGroup: FormGroup|null,
};

export default class ProgressBar {

    private static readonly FORM_GROUP_PREFIX = 'progressBar_';

    private static readonly INIT_PROGRESS = {
        form: undefined,
        currentGroup: 0,
        progress: 0,
        formIndexInGroup: 0,
        caption: null,
        progressGroup: null,
    }

    private static lastProgressAnimationFix: Progress|null = null;

    private readonly progressById: {[id :string]: Progress} = {};
    private readonly groupsCount;

    constructor(
        public readonly questionary: Questionary,
    ) {
        // precompute progress
        const forms = this.getFormsInRandomizedOrder();
        const progressGroups = forms.map(f => this.getClosestLevelGroup(f));

        const formsProgressGroupsUnique: FormGroup[] = [];
        const formsCountsInGroups = new Map<FormGroup, number>();
        progressGroups.forEach((g) => {
            if (g) {
                if (formsProgressGroupsUnique.indexOf(g) < 0) {
                    formsProgressGroupsUnique.push(g);
                }
                formsCountsInGroups.set(g, (formsCountsInGroups.get(g) || 0)+1);
            }
        });

        this.groupsCount = formsCountsInGroups.size;

        let indexInGroup = 0;
        forms.forEach((f, i) => {
            const previousProgress = this.progressById[forms[i-1]?.id] || {...ProgressBar.INIT_PROGRESS, form: f};
            const g = progressGroups[i];
            indexInGroup = (g && previousProgress.progressGroup === g) ? indexInGroup+1 : 0;
            if (!g) { // form is not in progress group
                this.progressById[f.id] = previousProgress;
                return;
            }
            this.progressById[f.id] = {
                progressGroup: g,
                formIndexInGroup: indexInGroup,
                currentGroup: formsProgressGroupsUnique.indexOf(g),
                form: f,
                caption: g.text,
                progress: (indexInGroup+1) / (formsCountsInGroups.get(g) || 1),
            }
        });
    }

    private getFormsInRandomizedOrder(): Form[] {
        const forms = [];
        let form: Form|null|undefined = this.questionary.getFirstForm();
        while(form) {
            forms.push(form);
            form = this.nextForm(form);
        }
        return forms;
    }

    private nextForm(form: Form): Form|undefined {
        const toFormId = this.questionary.randomizer!.translateMove(form, form.nextFormId || null)!;
        return this.questionary.formsById[toFormId];
    }

    /**
     * Get closest group with activated progressbar
     */
    private getClosestLevelGroup(form: FormItem): FormGroup|null {
        let actual = form;
        let parent = form.parent;
        while (parent) {
            if (actual instanceof FormGroup && actual.name.substr(0,ProgressBar.FORM_GROUP_PREFIX.length) === ProgressBar.FORM_GROUP_PREFIX) {
                return actual;
            }
            actual = parent;
            parent = actual.parent;
        }

        return null;
    }

    public view(): Vnode|null {
        const currentForm = this.questionary.currentForm;
        if (this.groupsCount === 0 || !currentForm) {
            return null;
        }
        const actualProgress = this.progressById[currentForm.id];
        if (!actualProgress) {
            return null;
        }

        // ProgressBar se vykreslí vlastně 2x, se zpožděním 1 frame aby se mohla překreslit animace
        // zpožďování musí jít přes statiskou proměnnou, protože ve view metodě se nedá zapisovat
        if (!ProgressBar.lastProgressAnimationFix) {
            ProgressBar.lastProgressAnimationFix = actualProgress;
        }
        const progress = ProgressBar.lastProgressAnimationFix;

        // Update o frame později
        if (ProgressBar.lastProgressAnimationFix !== actualProgress) {
            setTimeout(() => {
                ProgressBar.lastProgressAnimationFix = actualProgress;
                m.redraw();
            }, 16);
        }

        const bars = [];
        const captions = [];
        for (let i = 0; i<this.groupsCount; i++) {
            let bar, caption;
            if (i === progress.currentGroup) {
                bar = m('.bar.current', {}, [
                    m('.fill', {style: {width: (progress.progress*100)+'%'}})
                ]);
                const captionNumber = progress.progressGroup?.children.length ? (''+(progress.formIndexInGroup+1)+'/'+progress.progressGroup?.children.length) : '';
                caption = m('.caption.current', {}, [
                    m('span.caption-number', {}, captionNumber),
                    progress.caption ? m('span.caption-text', {}, m.trust(this.questionary.renderText(progress.caption))) : null,
                ]);
            } else {
                bar = m('.bar'+(i<progress.currentGroup?'.completed':'.coming'));
                caption = m('.caption');
            }
            bars.push(bar);
            captions.push(caption);
        }

        return m('.ProgressBar', {}, [
            m('.bars'+(progress.currentGroup>=this.groupsCount?'.completed':''), {}, bars),
            m('.captions', {}, captions),
        ]);
    }

}